import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_VISUALLY_HIDDEN,
} from "src/domains/shared/constants/styles/site";

/** 단계 미리보기 그림 칸(px) — 엔진 계산과 스타일이 같은 값을 쓴다 */
export const GUIDE_PEEK_SIZE = { width: 208, height: 130, frame: 222 } as const;

/** 가로 터치 화면에서 무대 높이에 못 쓰는 세로 — 머리말 + 위아래 틈 24px + 단계 막대 34px */
const LANDSCAPE_STAGE_CHROME_PX = SITE_HEADER_HEIGHT_PX + 24 + 34;
/** 가로 터치 화면 오른쪽 칸(지금 단계·재생 버튼·참고)의 최소 폭 */
const LANDSCAPE_ASIDE_MIN_WIDTH = "220px";

/**
 * 단계별 안내 플레이어(service-manual) — 그림(누르면 재생/일시정지, 양옆 가운데 이전·다음 화살표) → 바로 아래 단계 막대(한 칸 =
 * 한 단계, 올리면 그 단계 미리보기) → 지금 단계 이름 + 오른쪽 끝 재생/일시정지. 상태는 루트의 `data-ready`(첫 그림이 그려짐)·
 * `data-playing` 이다.
 */
export const GUIDE_STYLES = {
  /** 화면 읽기용 단계 목록(보이지 않음) — 무대처럼 화면 밖이면 계산을 건너뛴다 */
  stepList: { ...SITE_VISUALLY_HIDDEN, contentVisibility: "auto" },
  root: {
    overflowAnchor: "none",
    // 가로 터치 화면: 두 칸 — 왼쪽에 무대·단계 막대(main), 오른쪽에 지금 단계·재생 버튼(foot)과 그 절의 「참고」(aside)
    // 왼쪽 칸 폭 = 무대(16:10)와 단계 막대가 머리말 아래 화면 높이에 들어오는 폭. 오른쪽 칸이 좁아지면(장 옆 열이 있는 태블릿) 왼쪽이 양보한다
    [SITE_MEDIA.landscapeTouch]: {
      display: "grid",
      gridTemplateColumns: `minmax(0,calc((100svh - ${LANDSCAPE_STAGE_CHROME_PX}px) * 1.6)) minmax(${LANDSCAPE_ASIDE_MIN_WIDTH},1fr)`,
      columnGap: "24px",
      alignItems: "start",
    },
    "&:focus-visible": {
      outline: `2px solid ${SITE_COLOR.blue}`,
      outlineOffset: "6px",
      borderRadius: "24px",
    },
    // 첫 그림이 실제로 그려지기 전: 흐린 첫 장면(무대 바탕)만 — 그림·커서·강조 상자는 숨긴다(흰 화면·왼쪽 위 커서 금지)
    "&:not([data-ready]) [data-guide-layer]": { opacity: 0 },
    // 첫 그림이 그려지면 흐린 미리보기는 걷는다 — 전체 화면 장면의 위아래 여백은 옅은 회색으로
    "&[data-ready] [data-guide-stage]": { backgroundImage: "none !important" },
    "&:not([data-playing]) [data-guide-cursor]": { opacity: 0 },
    "&[data-playing] [data-label='play'], &:not([data-playing]) [data-label='pause']":
      { display: "none" },
  },
  main: {
    position: "relative",
    minWidth: 0,
    // 가로 터치 화면: 뿌리 격자의 왼쪽 칸(폭은 뿌리가 정한다) — 지금 단계·재생 버튼(foot)과 「참고」는 오른쪽 칸이라 세로에서 빠진다
    [SITE_MEDIA.landscapeTouch]: { gridColumn: 1, gridRow: "span 2" },
  },
  stage: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "22px",
    backgroundColor: SITE_COLOR.panel,
    backgroundSize: "cover",
    backgroundPosition: "center",
    boxShadow: `0 0 0 1px ${SITE_COLOR.line},0 10px 30px rgba(28,44,66,.07)`,
    aspectRatio: "16/10",
    cursor: "pointer",
    touchAction: "pan-y",
    WebkitTapHighlightColor: "transparent",
    // 화면 밖 플레이어(매뉴얼에 38개)는 무대·단계 막대·읽기용 단계 목록의 스타일·레이아웃 계산을 건너뛴다 — 회전·창 크기 바꿈처럼
    // 문서 전체를 다시 계산할 때 플레이어가 문서 요소의 77% 다. 세 곳 모두 크기가 정해져 있어(16:10 무대·높이 고정 막대·1px 목록)
    // 건너뛰는 동안에도 자리가 같다
    contentVisibility: "auto",
    // 휴대폰: 세로 무대(4:5) — 대화상자·패널처럼 세로로 긴 영역을 글자가 읽히는 배율로(단계마다 미리 고른 영역)
    [SITE_MEDIA.compact]: {
      borderRadius: "16px",
      aspectRatio: "4/5",
      maxHeight: "76svh",
    },
    // 가로 터치 화면은 좁아도 16:10 무대 — 엔진도 같은 조건에서 세로 영역(`mv`)을 쓰지 않는다(guideEngine)
    [SITE_MEDIA.landscapeTouch]: { aspectRatio: "16/10", maxHeight: "none" },
  },
  // 무대와 양옆 화살표를 겹쳐 두는 틀 — 화살표는 무대(누르면 재생/일시정지)의 형제라 누름이 무대로 가지 않는다
  stageWrap: {
    position: "relative",
    // 마우스로는 무대에 올렸을 때만 화살표를 보인다(보는 동안 가리지 않게)
    [SITE_MEDIA.hover]: {
      "&:hover [data-guide-arrow]:not(:disabled)": { opacity: 1 },
    },
  },
  // 무대 양옆 가운데의 이전·다음 단계 — 작게. 첫·마지막 단계에서는 그쪽이 비활성이라 감춘다
  arrow: {
    position: "absolute",
    top: "50%",
    zIndex: 5,
    width: "36px",
    height: "36px",
    marginTop: "-18px",
    padding: 0,
    border: 0,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,.92)",
    color: SITE_COLOR.ink,
    boxShadow: "0 2px 10px rgba(15,23,42,.16)",
    cursor: "pointer",
    transition: "opacity .2s,background-color .15s",
    "&[data-guide-arrow='prev']": { left: "12px" },
    "&[data-guide-arrow='next']": { right: "12px" },
    "& svg": { display: "block" },
    "&:hover": { background: "#fff" },
    "&:focus-visible": {
      opacity: 1,
      outline: `2px solid ${SITE_COLOR.blue}`,
      outlineOffset: "2px",
    },
    "&:disabled": { opacity: 0, visibility: "hidden" },
    [SITE_MEDIA.hover]: { opacity: 0 },
    [SITE_MEDIA.compact]: {
      width: "32px",
      height: "32px",
      marginTop: "-16px",
      "&[data-guide-arrow='prev']": { left: "8px" },
      "&[data-guide-arrow='next']": { right: "8px" },
    },
    [SITE_MEDIA.reducedMotion]: { transition: "none" },
  },
  // 장면 비율 그대로 — 무대가 세로(휴대폰)여도 찌그러지지 않게
  view: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    aspectRatio: "16/10",
    transformOrigin: "0 0",
    transition: `transform .6s ${SITE_EASE.out}`,
    willChange: "transform",
    "& img": {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      maxWidth: "none",
      display: "block",
      transition: "opacity .3s ease",
    },
    "& img[data-next]": {
      opacity: 0,
      transition: "opacity .26s ease",
      zIndex: 2,
    },
    "& img[data-next][data-on]": { opacity: 1 },
  },
  // 자리는 엔진이 `translate` 로 놓고(바뀔 때 미끄러지지 않게 전환 없음), 나타날 때 줄어드는 움직임은 `scale` 로만 준다
  ring: {
    zIndex: 3,
    position: "absolute",
    left: 0,
    top: 0,
    boxSizing: "border-box",
    border: `2.5px solid ${SITE_COLOR.blue}`,
    borderRadius: "6px",
    boxShadow: "0 0 0 4px rgba(29,116,255,.22)",
    opacity: 0,
    scale: "1.15",
    transition: `opacity .25s,scale .3s ${SITE_EASE.out}`,
    pointerEvents: "none",
    "&[data-on]": { opacity: 1, scale: "1" },
  },
  cursor: {
    position: "absolute",
    width: "26px",
    height: "26px",
    margin: "-2px 0 0 -2px",
    transformOrigin: "2px 2px",
    filter: "drop-shadow(0 2px 3px rgba(0,0,0,.35))",
    pointerEvents: "none",
    zIndex: 3,
    transition: "opacity .3s,transform .12s",
    "& svg": { display: "block" },
    "&[data-down]": {
      filter: "drop-shadow(0 1px 1px rgba(0,0,0,.4)) brightness(.85)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: "19px",
      top: "24px",
      width: "8px",
      height: "8px",
      borderRight: `2.5px solid ${SITE_COLOR.blue}`,
      borderBottom: `2.5px solid ${SITE_COLOR.blue}`,
      rotate: "45deg",
      opacity: 0,
    },
    "&[data-scroll]::after": { animation: "manualGuideScroll .52s ease-in 2" },
    // 위로 굴리기(3D 다가가기) — 같은 꺾쇠를 뒤집어 위로 흐르게
    "&[data-scroll='up']::after": {
      rotate: "225deg",
      animation: "manualGuideScrollUp .52s ease-in 2",
    },
    "@keyframes manualGuideScroll": {
      "0%": { opacity: 0, translate: "0 -5px" },
      "35%": { opacity: 1 },
      "100%": { opacity: 0, translate: "0 9px" },
    },
    "@keyframes manualGuideScrollUp": {
      "0%": { opacity: 0, translate: "0 5px" },
      "35%": { opacity: 1 },
      "100%": { opacity: 0, translate: "0 -9px" },
    },
    // 오른쪽 버튼 표시 — 커서 오른쪽 아래 작은 마우스, 오른쪽 버튼 칸만 파랗다
    "& [data-guide-button]": {
      position: "absolute",
      left: "20px",
      top: "17px",
      color: SITE_COLOR.blue,
      opacity: 0,
      scale: ".8",
      transition: `opacity .2s,scale .25s ${SITE_EASE.out}`,
    },
    "&[data-button='right'] [data-guide-button]": { opacity: 1, scale: "1" },
    [SITE_MEDIA.reducedMotion]: {
      "&[data-scroll]::after": { animation: "none", opacity: 1 },
      "& [data-guide-button]": { transition: "none" },
    },
  },
  // 그림을 눌렀을 때 그 자리에서 재생/일시정지가 됐다는 표시(잠깐 떴다 사라짐)
  flash: {
    position: "absolute",
    left: "50%",
    top: "50%",
    zIndex: 4,
    width: "68px",
    height: "68px",
    margin: "-34px 0 0 -34px",
    borderRadius: "50%",
    background: "rgba(15,23,42,.5)",
    color: "#fff",
    display: "grid",
    placeItems: "center",
    opacity: 0,
    pointerEvents: "none",
    "& svg": { display: "none" },
    "&[data-icon='play'] [data-icon-kind='play'], &[data-icon='pause'] [data-icon-kind='pause']":
      { display: "block" },
    "&[data-on]": { animation: "manualGuideFlash .65s ease-out" },
    "@keyframes manualGuideFlash": {
      "0%": { opacity: 0, transform: "scale(.85)" },
      "25%": { opacity: 1, transform: "scale(1)" },
      "100%": { opacity: 0, transform: "scale(1.12)" },
    },
  },
  // 단계 막대: 한 칸 = 한 단계(지난 칸 파랑, 지금 칸은 차오름, 남은 칸 회색). 막대는 4px 이지만 누르는 영역은 칸 전체 24px
  nav: { position: "relative", marginTop: "10px" },
  segments: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    gap: "4px",
    contentVisibility: "auto",
    containIntrinsicBlockSize: "auto 24px",
    "& li": { flex: "1 1 0", minWidth: 0 },
    "& button": {
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: "24px",
      padding: 0,
      border: 0,
      background: "none",
      cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
    },
    "& i": {
      display: "block",
      flex: 1,
      height: "4px",
      borderRadius: "3px",
      background: "rgba(0,0,0,.1)",
      overflow: "hidden",
      transition: "height .15s,background-color .15s",
    },
    "& b": {
      display: "block",
      width: "100%",
      height: "100%",
      background: SITE_COLOR.blue,
      transform: "scaleX(0)",
      transformOrigin: "0 50%",
    },
    "& li[data-done] b": { transform: "scaleX(1)" },
    "& li[aria-current] i": { background: "rgba(29,116,255,.2)" },
    "& button:hover i, & button:focus-visible i": {
      height: "8px",
      background: "rgba(0,0,0,.18)",
    },
    "& li[aria-current] button:hover i": { background: "rgba(29,116,255,.28)" },
    "& button:focus-visible": { outline: "none" },
    [SITE_MEDIA.compact]: {
      gap: "3px",
      containIntrinsicBlockSize: "auto 30px",
      "& button": { height: "30px" },
    },
  },
  // 미리보기: 막대 위, 올린 칸 가운데(그림 밖으로 나가지 않게). 서서히 나타나고 사라진다 — 가로 자리(left)는 전환하지 않아
  // 다른 칸에서 다시 나타날 때 옛 자리에서 미끄러지지 않는다
  peek: {
    position: "absolute",
    bottom: "calc(100% + 2px)",
    zIndex: 6,
    width: `${GUIDE_PEEK_SIZE.frame}px`,
    padding: "7px",
    borderRadius: "14px",
    background: "#fff",
    boxShadow: "0 14px 36px rgba(28,44,66,.24),0 0 0 1px rgba(0,0,0,.06)",
    pointerEvents: "none",
    opacity: 0,
    visibility: "hidden",
    transform: "translateY(4px) scale(.98)",
    transformOrigin: "50% 100%",
    transition:
      "opacity .16s ease,transform .16s ease,visibility 0s linear .16s",
    "&[data-on]": {
      opacity: 1,
      visibility: "visible",
      transform: "none",
      transition: `opacity .2s ease,transform .2s ${SITE_EASE.out},visibility 0s`,
    },
    [SITE_MEDIA.reducedMotion]: {
      transition: "none",
      "&[data-on]": { transition: "none" },
    },
  },
  peekImage: {
    position: "relative",
    width: `${GUIDE_PEEK_SIZE.width}px`,
    height: `${GUIDE_PEEK_SIZE.height}px`,
    borderRadius: "9px",
    overflow: "hidden",
    background: SITE_COLOR.panel,
    "& img": { position: "absolute", maxWidth: "none", display: "block" },
    "& i": {
      position: "absolute",
      border: `2px solid ${SITE_COLOR.blue}`,
      borderRadius: "4px",
      boxShadow: "0 0 0 3px rgba(29,116,255,.22)",
    },
  },
  peekCaption: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    margin: 0,
    padding: "8px 3px 2px",
    fontSize: "13.5px",
    fontWeight: 600,
    lineHeight: 1.4,
    color: SITE_COLOR.ink,
    "& b": { color: SITE_COLOR.blueDark, fontVariantNumeric: "tabular-nums" },
  },
  // 지금 단계 이름(자막) + 오른쪽 끝 재생/일시정지
  foot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginTop: "4px",
    minHeight: "36px",
    [SITE_MEDIA.compact]: { alignItems: "flex-start" },
    // 가로 터치 화면: 오른쪽 칸 위 — 지금 단계·제목 아래 재생 버튼
    [SITE_MEDIA.landscapeTouch]: {
      gridColumn: 2,
      marginTop: 0,
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "10px",
    },
  },
  caption: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    minWidth: 0,
    margin: 0,
    [SITE_MEDIA.compact]: { flexWrap: "wrap", paddingTop: "7px" },
    [SITE_MEDIA.landscapeTouch]: { flexWrap: "wrap", paddingTop: "4px" },
  },
  number: {
    fontSize: "16px",
    lineHeight: 1,
    fontWeight: 800,
    color: SITE_COLOR.blueDark,
    fontVariantNumeric: "tabular-nums",
  },
  count: {
    fontSize: "14px",
    color: SITE_COLOR.ink3,
    fontVariantNumeric: "tabular-nums",
  },
  title: {
    marginLeft: "8px",
    fontSize: "16px",
    fontWeight: 600,
    letterSpacing: "-.01em",
    color: SITE_COLOR.ink,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [SITE_MEDIA.compact]: { whiteSpace: "normal", marginLeft: "4px" },
    // 가로 터치 화면: 좁은 오른쪽 칸에서 단계 번호 아래 줄에 제목 전체
    [SITE_MEDIA.landscapeTouch]: {
      flexBasis: "100%",
      whiteSpace: "normal",
      marginLeft: 0,
    },
  },
  /** 절의 「참고」 자리 — 평소엔 플레이어 아래, 가로 터치 화면은 오른쪽 칸 재생 버튼 아래 */
  aside: {
    [SITE_MEDIA.landscapeTouch]: {
      gridColumn: 2,
      "& > p": { marginTop: "16px" },
    },
  },
  toggle: {
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    height: "34px",
    padding: "0 13px 0 11px",
    borderRadius: "17px",
    border: `1px solid ${SITE_COLOR.line}`,
    background: "#fff",
    font: "inherit",
    fontSize: "13.5px",
    fontWeight: 600,
    color: SITE_COLOR.ink2,
    cursor: "pointer",
    transition: "background-color .15s,color .15s",
    "& span": { display: "inline-flex", alignItems: "center", gap: "6px" },
    "&:hover": { background: SITE_COLOR.grey100, color: SITE_COLOR.ink },
  },
} as const;
